import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  computeAnchoredPopoverPanelStyle,
  computeDockLeftPanelStyle,
  parsePopoverPanelMaxHeightPx,
  resolveDockLeftTopPx,
  shouldUseAnchoredPlacement,
  type EventqDialogPopoverPanelStyle,
} from './dialog-popover-position';

export type EventqDialogPopoverPlacement =
  | 'center'
  | 'anchor-top-end'
  | 'anchor-right'
  | 'dock-left';

const MAX_ANCHOR_POSITION_RETRIES = 12;

@Component({
  selector: 'eventq-dialog-popover',
  imports: [MatIcon],
  templateUrl: './dialog-popover.html',
  styleUrl: './dialog-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqDialogPopover {
  private static nextTitleId = 0;

  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  private anchorRetryCount = 0;
  private anchorRetryFrame: number | null = null;
  private portalParent: Node | null = null;
  private portalNextSibling: ChildNode | null = null;
  private portaledToBody = false;

  readonly titleId = `eventq-dialog-popover-title-${EventqDialogPopover.nextTitleId++}`;

  readonly open = input(false);
  readonly title = input.required<string>();
  /** Optional Material icon name shown before the title (e.g. `verified_user`). */
  readonly titleIcon = input<string | null>(null);
  readonly cancelLabel = input('Cancel');
  readonly confirmLabel = input('Confirm');
  readonly confirmDisabled = input(false);
  readonly anchor = input<HTMLElement | null>(null);
  readonly placement = input<EventqDialogPopoverPlacement>('center');
  readonly panelWidth = input<string | null>(null);
  /**
   * Optional forced panel height (center placement). Prefer `panelMaxHeight` alone
   * when the panel should shrink with content.
   */
  readonly panelHeight = input<string | null>(null);
  /** Caps panel height; does not force height unless `panelHeight` is also set. */
  readonly panelMaxHeight = input<string | null>(null);
  readonly panelClass = input('');
  readonly showFooter = input(true);
  /** When false, footer shows only the cancel/close action (e.g. Close-only lists). */
  readonly showConfirm = input(true);
  /**
   * When false, the body never scrolls itself — required for projected content
   * that owns its own scroll region (e.g. a fill-height searchable list), which
   * would otherwise produce a second nested scrollbar.
   */
  readonly bodyScroll = input(true);
  /** When true, omit built-in header so callers can project Figma chrome. */
  readonly hideHeader = input(false);

  readonly closed = output<void>();
  readonly confirmed = output<void>();

  readonly panelStyle = signal<Partial<EventqDialogPopoverPanelStyle>>({});
  readonly isAnchored = signal(false);

  readonly centerPanelStyle = computed(() => {
    if (this.isAnchored()) {
      return {};
    }

    const styles: Record<string, string> = {};
    const width = this.panelWidth();
    const height = this.panelHeight();
    const maxHeight = this.panelMaxHeight();

    if (width) {
      styles['width'] = `min(${width}, calc(100vw - 32px))`;
    }

    if (height) {
      styles['height'] = `min(${height}, calc(100vh - 32px))`;
    }

    if (maxHeight) {
      styles['max-height'] = `min(${maxHeight}, calc(100vh - 32px))`;
    }

    return styles;
  });

  constructor() {
    afterNextRender(() => this.updatePanelPosition());

    effect(() => {
      this.open();
      this.anchor();
      this.placement();
      this.panelWidth();
      this.panelHeight();
      this.panelMaxHeight();
      this.updatePanelPosition();
    });

    this.destroyRef.onDestroy(() => {
      this.clearAnchorRetry();
      this.restorePortal();
      this.host.nativeElement.classList.remove('eventq-dialog-popover-host--anchored');
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.onClose();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    this.updatePanelPosition();
  }

  onClose(): void {
    this.closed.emit();
  }

  onConfirm(): void {
    if (this.confirmDisabled()) {
      return;
    }

    this.confirmed.emit();
  }

  private updatePanelPosition(): void {
    const anchor = this.anchor();
    const placement = this.placement();

    if (!this.open() || !shouldUseAnchoredPlacement(placement, anchor)) {
      this.clearAnchorRetry();
      this.anchorRetryCount = 0;
      this.restorePortal();
      this.panelStyle.set({});
      this.isAnchored.set(false);
      this.host.nativeElement.classList.remove('eventq-dialog-popover-host--anchored');
      return;
    }

    if (placement === 'dock-left') {
      this.clearAnchorRetry();
      this.anchorRetryCount = 0;
      this.host.nativeElement.classList.add('eventq-dialog-popover-host--anchored');
      this.isAnchored.set(true);
      this.portalToBody();
      this.panelStyle.set(
        computeDockLeftPanelStyle(
          this.panelWidth(),
          window.innerWidth,
          window.innerHeight,
          resolveDockLeftTopPx(anchor),
        ),
      );
      return;
    }

    if (!anchor) {
      this.scheduleAnchorRetry();
      return;
    }

    this.anchorRetryCount = 0;
    const rect = anchor.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) {
      this.scheduleAnchorRetry();
      return;
    }

    this.host.nativeElement.classList.add('eventq-dialog-popover-host--anchored');
    this.isAnchored.set(true);
    this.portalToBody();
    this.panelStyle.set(
      computeAnchoredPopoverPanelStyle(
        rect,
        this.panelWidth(),
        window.innerWidth,
        placement,
        window.innerHeight,
        parsePopoverPanelMaxHeightPx(this.panelMaxHeight()),
      ),
    );
  }

  /** Escape nested popover stacking contexts (e.g. View Alarm modal) for correct fixed layout. */
  private portalToBody(): void {
    if (this.portaledToBody || typeof document === 'undefined') {
      return;
    }

    const host = this.host.nativeElement;
    if (host.parentNode === document.body) {
      this.portaledToBody = true;
      return;
    }

    this.portalParent = host.parentNode;
    this.portalNextSibling = host.nextSibling;
    document.body.appendChild(host);
    this.portaledToBody = true;
  }

  private restorePortal(): void {
    if (!this.portaledToBody || typeof document === 'undefined') {
      return;
    }

    const host = this.host.nativeElement;
    if (this.portalParent) {
      if (this.portalNextSibling) {
        this.portalParent.insertBefore(host, this.portalNextSibling);
      } else {
        this.portalParent.appendChild(host);
      }
    }

    this.portalParent = null;
    this.portalNextSibling = null;
    this.portaledToBody = false;
  }

  private scheduleAnchorRetry(): void {
    if (this.anchorRetryFrame !== null || this.anchorRetryCount >= MAX_ANCHOR_POSITION_RETRIES) {
      return;
    }

    this.anchorRetryCount += 1;
    this.anchorRetryFrame = requestAnimationFrame(() => {
      this.anchorRetryFrame = null;
      this.updatePanelPosition();
    });
  }

  private clearAnchorRetry(): void {
    if (this.anchorRetryFrame !== null) {
      cancelAnimationFrame(this.anchorRetryFrame);
      this.anchorRetryFrame = null;
    }
  }
}
